import { Annotation, AnnotationSource, isCollectionAnnotation } from "@perceptron/core/types";

/**
 * Whole-response lookups, derived once per response rather than once per
 * component.
 *
 * `useAnnotationInteractions` runs in every annotation component, so anything
 * it derives in a `useMemo` is derived again for each one — O(components x
 * annotations) per streamed frame. These caches key on the identity of the
 * annotation arrays, so the first component to ask does the work and the rest
 * read it. That holds because `store/annotation-identity.ts` keeps those
 * identities stable across re-parses.
 */
export interface AnnotationIndex {
  /** Every annotation, collections included, in document order. */
  readonly flattened: readonly Annotation[];
  /** Id to annotation, replacing a linear scan per lookup. */
  readonly byId: ReadonlyMap<string, Annotation>;
}

const indexCache = new WeakMap<readonly Annotation[], AnnotationIndex>();
const mergeCache = new WeakMap<readonly Annotation[], WeakMap<readonly Annotation[], readonly Annotation[]>>();
const idsCache = new WeakMap<readonly Annotation[], ReadonlySet<string>>();

function collect(node: Annotation, flattened: Annotation[], byId: Map<string, Annotation>): void {
  flattened.push(node);
  byId.set(node.id, node);
  if (isCollectionAnnotation(node)) {
    for (const child of node.children) collect(child, flattened, byId);
  }
}

/** The index for these annotations, computed on first ask and then reused. */
export function annotationIndex(nodes: readonly Annotation[]): AnnotationIndex {
  const cached = indexCache.get(nodes);
  if (cached) return cached;

  const flattened: Annotation[] = [];
  const byId = new Map<string, Annotation>();
  for (const node of nodes) collect(node, flattened, byId);

  const index: AnnotationIndex = { flattened, byId };
  indexCache.set(nodes, index);
  return index;
}

/**
 * Reasoning and content annotations as one list, stable for a given pair so
 * every component shares the same array — and therefore the same index.
 */
export function mergeAnnotationSets(
  reasoning: readonly Annotation[],
  content: readonly Annotation[],
): readonly Annotation[] {
  let byContent = mergeCache.get(reasoning);
  if (!byContent) {
    byContent = new WeakMap();
    mergeCache.set(reasoning, byContent);
  }
  const cached = byContent.get(content);
  if (cached) return cached;

  const merged = [...reasoning, ...content];
  byContent.set(content, merged);
  return merged;
}

/** The ids in one asset's slice, for mask visibility checks. */
export function annotationIdsIn(nodes: readonly Annotation[]): ReadonlySet<string> {
  const cached = idsCache.get(nodes);
  if (cached) return cached;
  const ids = new Set<string>();
  const walk = (node: Annotation) => {
    ids.add(node.id);
    if (isCollectionAnnotation(node)) node.children.forEach(walk);
  };
  nodes.forEach(walk);
  idsCache.set(nodes, ids);
  return ids;
}

/**
 * Which annotations the overlays should show, given what is hovered or pinned.
 *
 * Derived here rather than in a per-instance `useMemo` because three of its
 * four branches build a fresh array: done per component, every reasoning-scoped
 * hover would hand each one its own array, and with it its own index, its own
 * slices and its own id set — exactly the per-component work this module exists
 * to remove. One slot is enough: every component renders against the same store
 * values in a given commit, so the first call fills it and the rest hit it.
 */
export interface SourceScopeInputs {
  annotations: readonly Annotation[];
  reasoningAnnotations: readonly Annotation[];
  flattened: readonly Annotation[];
  clickedAnnotationId: string | undefined;
  clickedAnnotationSource: AnnotationSource | undefined;
  highlightedAnnotationIds: string[] | undefined;
  highlightedAnnotationSource: AnnotationSource | undefined;
  pinnedVideoAnnotation: Annotation | undefined;
  /** Retains a selected collection once, without duplicating its selected descendants. */
  selectRoots: (annotation: Annotation, ids: ReadonlySet<string>) => Annotation[];
}

let lastScopeInputs: SourceScopeInputs | null = null;
let lastScopeResult: readonly Annotation[] = [];

function sameScopeInputs(a: SourceScopeInputs, b: SourceScopeInputs): boolean {
  return (
    a.annotations === b.annotations &&
    a.reasoningAnnotations === b.reasoningAnnotations &&
    a.flattened === b.flattened &&
    a.clickedAnnotationId === b.clickedAnnotationId &&
    a.clickedAnnotationSource === b.clickedAnnotationSource &&
    a.highlightedAnnotationIds === b.highlightedAnnotationIds &&
    a.highlightedAnnotationSource === b.highlightedAnnotationSource &&
    a.pinnedVideoAnnotation === b.pinnedVideoAnnotation
  );
}

export function sourceScopedAnnotations(inputs: SourceScopeInputs): readonly Annotation[] {
  if (lastScopeInputs && sameScopeInputs(lastScopeInputs, inputs)) return lastScopeResult;

  const {
    annotations,
    reasoningAnnotations,
    flattened,
    clickedAnnotationId,
    clickedAnnotationSource,
    highlightedAnnotationIds,
    highlightedAnnotationSource,
    pinnedVideoAnnotation,
    selectRoots,
  } = inputs;

  const visibleReasoningIds = new Set<string>();
  if (clickedAnnotationId && clickedAnnotationSource === AnnotationSource.REASONING) {
    visibleReasoningIds.add(clickedAnnotationId);
  }
  if (highlightedAnnotationIds?.length && highlightedAnnotationSource === AnnotationSource.REASONING) {
    highlightedAnnotationIds.forEach((id) => visibleReasoningIds.add(id));
  }

  let result: readonly Annotation[];
  if (visibleReasoningIds.size === 0) {
    result = annotations;
  } else if (pinnedVideoAnnotation) {
    // Keep a video pin's tree mounted while previewing another source; removing
    // its timeline node would discard the pin and its completed tracking work.
    const reasoning = reasoningAnnotations.flatMap((annotation) => selectRoots(annotation, visibleReasoningIds));
    const includeContent =
      pinnedVideoAnnotation.source === AnnotationSource.CONTENT ||
      (highlightedAnnotationIds?.length && highlightedAnnotationSource === AnnotationSource.CONTENT);
    result = includeContent ? [...annotations, ...reasoning] : reasoning;
  } else {
    result = flattened.filter((a) => a.source === AnnotationSource.REASONING && visibleReasoningIds.has(a.id));
  }

  lastScopeInputs = inputs;
  lastScopeResult = result;
  return result;
}
